import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { theme } from '../theme'

const STORAGE_KEY = 'ks_consent_state'

type ConsentState = 'granted' | 'denied' | 'pending' | 'no-cmp'

const sectionTitle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 16px',
}

const card: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  marginBottom: '20px',
}

const stepNum: CSSProperties = {
  flexShrink: 0,
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: theme.accentSubtle,
  border: `1px solid ${theme.accent}`,
  color: theme.accent,
  fontSize: '11px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const code = (text: string) => (
  <code
    style={{
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: theme.accent,
      backgroundColor: theme.accentSubtle,
      padding: '1px 5px',
      borderRadius: '3px',
    }}
  >
    {text}
  </code>
)

function pushConsentDefault(state: 'granted' | 'denied' | 'pending'): void {
  if (!Array.isArray(window.dataLayer)) window.dataLayer = []
  const params = state === 'pending' ? {} : { analytics_storage: state }
  window.dataLayer.push(['consent', 'default', params])
}

function pushConsentUpdate(state: 'granted' | 'denied'): void {
  if (!Array.isArray(window.dataLayer)) window.dataLayer = []
  window.dataLayer.push(['consent', 'update', { analytics_storage: state }])
}

type ConsentBannerProps = { onResolve: (state: 'granted' | 'denied') => void }

const ConsentBanner = ({ onResolve }: ConsentBannerProps) => (
  <div
    style={{
      position: 'fixed',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'min(520px, calc(100vw - 48px))',
      backgroundColor: '#1a1a1e',
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      padding: '20px 24px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '16px' }}>🍪</span>
      <span style={{ fontSize: '13px', fontWeight: 700, color: theme.text }}>
        Cookie preferences
      </span>
    </div>
    <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted, lineHeight: '1.6' }}>
      We use analytics cookies to understand how visitors interact with our site. You can accept or
      decline below. Your preference is saved and respected on every page load.
    </p>
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
      <button
        data-test-id='consent-decline'
        onClick={() => onResolve('denied')}
        style={{
          backgroundColor: 'transparent',
          color: theme.textMuted,
          border: `1px solid ${theme.border}`,
          borderRadius: '7px',
          padding: '7px 16px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Decline
      </button>
      <button
        data-test-id='consent-accept'
        onClick={() => onResolve('granted')}
        style={{
          backgroundColor: theme.accent,
          color: '#fff',
          border: 'none',
          borderRadius: '7px',
          padding: '7px 16px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Accept all
      </button>
    </div>
  </div>
)

export const ConsentPage = () => {
  const [consentState, setConsentState] = useState<ConsentState>(() => {
    const val = localStorage.getItem(STORAGE_KEY)
    if (val === 'granted' || val === 'denied') return val
    return 'no-cmp'
  })
  const [showBanner, setShowBanner] = useState(false)

  // On mount: if there's a stored choice, push it as default before init runs.
  // If no stored choice, do nothing — no CMP presence, embed tracks normally.
  useEffect(() => {
    const val = localStorage.getItem(STORAGE_KEY)
    if (val === 'granted' || val === 'denied') {
      pushConsentDefault(val)
    }
  }, [])

  const handleShowBanner = () => {
    // Signals CMP is present but pending — triggers embed's optimistic Option D
    pushConsentDefault('pending')
    setConsentState('pending')
    setShowBanner(true)
  }

  const handleBannerResolve = (state: 'granted' | 'denied') => {
    pushConsentUpdate(state)
    localStorage.setItem(STORAGE_KEY, state)
    setConsentState(state)
    setShowBanner(false)
  }

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY)
    setConsentState('no-cmp')
    setShowBanner(false)
  }

  const statusMap: Record<
    ConsentState,
    { color: string; bg: string; border: string; dot: string; label: string }
  > = {
    'no-cmp': {
      color: theme.textMuted,
      bg: theme.surfaceElevated,
      border: theme.border,
      dot: '◌',
      label: 'No CMP — banner not shown',
    },
    pending: {
      color: '#fbbf24',
      bg: 'rgba(251, 191, 36, 0.1)',
      border: 'rgba(251, 191, 36, 0.2)',
      dot: '◑',
      label: 'Banner shown — awaiting choice',
    },
    granted: {
      color: theme.success,
      bg: theme.successSubtle,
      border: 'rgba(34, 197, 94, 0.2)',
      dot: '●',
      label: 'Consent granted',
    },
    denied: {
      color: '#f87171',
      bg: 'rgba(248, 113, 113, 0.1)',
      border: 'rgba(248, 113, 113, 0.2)',
      dot: '○',
      label: 'Consent denied',
    },
  }

  const status = statusMap[consentState]

  return (
    <>
      {showBanner && <ConsentBanner onResolve={handleBannerResolve} />}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: theme.accent,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              backgroundColor: theme.accentSubtle,
              padding: '3px 8px',
              borderRadius: '4px',
            }}
          >
            Google Consent Mode v2
          </span>
        </div>

        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: theme.text,
            margin: '0 0 6px',
            letterSpacing: '-0.02em',
          }}
        >
          🍪 CMP Simulation
        </h1>
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
          A real cookie banner using the Google Consent Mode v2 API ({code('dataLayer.push')}).
          Covers all states: no CMP, pending, granted, denied. Persists across reloads via{' '}
          {code('localStorage')}.
        </p>

        {/* Status + controls */}
        <div style={card}>
          <p style={sectionTitle}>Current state</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: status.color,
                backgroundColor: status.bg,
                padding: '5px 12px',
                borderRadius: '6px',
                border: `1px solid ${status.border}`,
              }}
            >
              <span>{status.dot}</span>
              {status.label}
            </div>

            {consentState === 'no-cmp' && (
              <button
                data-test-id='show-banner'
                onClick={handleShowBanner}
                style={{
                  backgroundColor: theme.surfaceElevated,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '7px',
                  padding: '5px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Show banner
              </button>
            )}

            {consentState !== 'no-cmp' && (
              <button
                data-test-id='reset-consent'
                onClick={handleReset}
                style={{
                  backgroundColor: theme.surfaceElevated,
                  color: theme.textMuted,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '7px',
                  padding: '5px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Reset (remove CMP)
              </button>
            )}
          </div>
        </div>

        {/* Steps */}
        <div style={card}>
          <p style={sectionTitle}>Test steps</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              {
                step: '1',
                text: (
                  <>
                    <strong style={{ color: theme.text }}>No CMP</strong>: initial state — no{' '}
                    {code('dataLayer')} push. Click{' '}
                    <strong style={{ color: theme.text }}>🔥 Initialize</strong> — embed detects no
                    CMP and tracks normally. Verify {code('/api/s/page')} fires.
                  </>
                ),
              },
              {
                step: '2',
                text: (
                  <>
                    Click <strong style={{ color: theme.text }}>Show banner</strong>. A{' '}
                    {code("['consent', 'default', {}]")} is pushed (CMP present, pending). Tracking
                    continues optimistically (Option D). The banner appears — don't click anything
                    yet.
                  </>
                ),
              },
              {
                step: '3',
                text: (
                  <>
                    Click <strong style={{ color: '#fca5a5' }}>Decline</strong> in the banner. A{' '}
                    {code("['consent', 'update', { analytics_storage: 'denied' }]")} is pushed.
                    Verify {code('/api/s/')} requests stop immediately.
                  </>
                ),
              },
              {
                step: '4',
                text: (
                  <>
                    <strong style={{ color: theme.text }}>Denied persistence</strong>: reload. No
                    banner shown, {code('denied')} default pushed before init. Click{' '}
                    <strong style={{ color: theme.text }}>🔥 Initialize</strong> — no tracking
                    requests should fire.
                  </>
                ),
              },
              {
                step: '5',
                text: (
                  <>
                    Click <strong style={{ color: theme.text }}>Reset</strong>, then{' '}
                    <strong style={{ color: theme.text }}>Show banner</strong>, then{' '}
                    <strong style={{ color: theme.accent }}>Accept all</strong>. Tracking resumes.
                    Reload — {code('granted')} default fires before init, tracking starts
                    immediately with no banner.
                  </>
                ),
              },
            ].map(({ step, text }) => (
              <div key={step} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={stepNum}>{step}</div>
                <p
                  style={{ margin: 0, fontSize: '13px', color: theme.textMuted, lineHeight: '1.6' }}
                >
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
